import { ref, shallowRef, watch } from 'vue';

/**
 * Loads `fetcher(source)` whenever the reactive `source` changes. A response
 * that arrives after a newer request started is dropped, so fast typing or
 * quick navigation never shows stale results.
 *
 * `source` returning null/undefined means "nothing to load" and clears data.
 */
export function useAsyncData(source, fetcher, { immediate = true, keepPrevious = false } = {}) {
  const data = shallowRef(null);
  const error = shallowRef(null);
  const loading = ref(false);
  let ticket = 0;

  async function load(value = source()) {
    const mine = ++ticket;
    error.value = null;

    if (value == null) {
      data.value = null;
      loading.value = false;
      return;
    }

    if (!keepPrevious) data.value = null;
    loading.value = true;
    try {
      const result = await fetcher(value);
      if (mine === ticket) data.value = result;
    } catch (e) {
      if (mine === ticket) error.value = e;
    } finally {
      if (mine === ticket) loading.value = false;
    }
  }

  watch(source, (value) => load(value), { immediate, deep: true });

  return { data, error, loading, reload: () => load() };
}
